import { fetchFromLambda, fetchFromApi } from '@agyletime/vue-3-common/src/utils/apiService';
import moment from 'moment';
import 'moment-timezone';
import { BaseModel } from '../../types';
import { getAllTeams, getInactiveTeams } from '@agyletime/vue-3-common/src/services/teams';
import { getAllUsers } from '@agyletime/vue-3-common/src/services/user';
import type { Team } from '@agyletime/vue-3-common/src/typings/team';
import { UserWithEndAt } from '@/types/interactionSummary';

type MyOrganisation = {
  id: number;
  customDomain: string;
  name: string;
  startWeekOn: number;
  subDomain: string;
  timezone: string;
  __v: number;
  _id: string;
} | undefined;
type UserFilterOption = {
  activeFlag?: string; // active, inactive, all
  email?: string;
  returnMinimalDetails?: string; // none
};
type FilterOption = {
  teams?: {
    queryParams?: string; // active=Y, active=N
  };
  users?: UserFilterOption;
  workstreams?: {
    queryParams?: string; // isDeleted=true
  };
};

let _orgDetails: MyOrganisation = undefined;
const getMyOrganisation = async (): Promise<MyOrganisation> => {
    return await fetchFromLambda('/organisation/organisations/me');
};
const orgDetails = async () => {
    if (!_orgDetails) {
        try {
            _orgDetails = await getMyOrganisation();
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Unable to retrieve organisation details', e);
        }
    }
    return _orgDetails;
};
const getLocaleWeek = async () => {
    const organisation = await orgDetails();

    return { week: { dow: organisation?.startWeekOn || 1 } };
};
export const initialiseLocale = async (): Promise<MyOrganisation> => {
    moment.updateLocale(moment.locale(), await getLocaleWeek());
    return await orgDetails();
};

const appendQueryParams = (url: string, params?: string, separator?: string): string => {
    if (!params) return url;

    return `${url}${separator}${params}`;
};

export const loadFilterDependencies = async (option?: FilterOption) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allPromise: any[] = await Promise.all([
        getAllTeams(),
        getAllUsers(1000, 'all'),
        fetchFromLambda('/organisation/business-roles'),
        fetchFromLambda(appendQueryParams('/workactivity/workstream-definitions', option?.workstreams?.queryParams, '?')),
        // for data references
        getInactiveTeams(),
        fetchFromLambda(appendQueryParams('/workactivity/workstream-definitions', 'isDeleted=true', '?')),
        fetchFromApi(appendQueryParams('/api/schedule/tasks/definitions', undefined, '?')),
        fetchFromApi(appendQueryParams('/api/schedule/tasks/definitions', 'showDeleted=Y', '?'))
    ]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const teams: Team[] = allPromise[0].map((team: any) => {
        let parentGroupName = team.parentGroupName;
        if (team.deletedAt) parentGroupName = '一 Deleted';
        else if (!team.parentId) parentGroupName = '一 No Group';

        return {
            name: team.name,
            id: team.id,
            parentGroupName
        };
    });
    teams.sort((a, b) => {
        return a.name.localeCompare(b.name);
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const activeWorkstreams: BaseModel[] = allPromise[3].map((workstream: any) => {
        return {
            id: workstream.id,
            name: workstream.name
        };
    });
    activeWorkstreams.sort((a, b) => {
        return a.name.localeCompare(b.name);
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const roles: BaseModel[] = allPromise[2].map((role: any) => {
        return {
            id: role.id,
            name: role.name
        };
    });
    roles.sort((a, b) => {
        return a.name.localeCompare(b.name);
    });

    const timezones = moment.tz.names().map((timezone: string) => {
        return { value: timezone };
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inactiveTeams: Team[] = allPromise[4].map((team: any) => {
        let parentGroupName = team.parentGroupName;
        if (team.deletedAt) parentGroupName = '一 Deleted';
        else if (!team.parentId) parentGroupName = '一 No Group';

        return {
            name: team.name,
            id: team.id,
            parentGroupName
        };
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deletedWorkstreams: BaseModel[] = allPromise[5].map((workstream: any) => {
        return {
            id: workstream.id,
            name: workstream.name
        };
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const activeWorkloads: BaseModel[] = allPromise[6].map((workload: any) => {
        return {
            id: workload.id,
            name: workload.name
        };
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deletedWorkloads: BaseModel[] = allPromise[7].map((workload: any) => {
        return {
            id: workload.id,
            name: workload.name
        };
    });

    return {
        teams,
        workstreams: activeWorkstreams,
        roles,
        timezones,
        users: allPromise[1] as UserWithEndAt[],
        allTeams: teams.concat(inactiveTeams),
        allWorkstreams: activeWorkstreams.concat(deletedWorkstreams),
        allWorkloads: activeWorkloads.concat(deletedWorkloads)
    };
};
